import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardApi, entityApi, aiApi } from '../../lib/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { getEntityTypeColor } from '../../lib/utils';
import { 
  Building2, Sparkles, TrendingUp, Globe, Plus, ArrowRight, Loader2,
  Landmark, Shield, Briefcase, GitBranch, Network, Download, Coins
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import GettingStartedTour from '../../components/tour/GettingStartedTour';

const entityIcons = {
  HOLDING: Landmark,
  LLC: Building2,
  TRUST: Shield,
  CORPORATION: Briefcase,
  SUBSIDIARY: GitBranch
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [entities, setEntities] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, entitiesRes, recsRes, creditsRes] = await Promise.all([
        dashboardApi.getStats(),
        entityApi.getAll(),
        aiApi.getRecommendations(),
        api.get('/formation/credits').catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setEntities(entitiesRes.data);
      setRecommendations(recsRes.data);
      setCredits(creditsRes.data);
    } catch (err) {
      console.error('Dashboard load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportAuditReport = async () => {
    setExporting(true);
    try {
      const response = await api.post('/export/audit-report');
      toast.success('Audit report generated and sent to your email!');
      
      // Optionally download the PDF
      if (response.data.pdf_data) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${response.data.pdf_data}`;
        link.download = `${response.data.report_name}.pdf`;
        link.click();
      }
    } catch (error) {
      toast.error('Failed to generate audit report');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  const latestRec = recommendations[0];
  const topJurisdictions = Object.entries(stats?.entities_by_jurisdiction || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <DashboardLayout>
      {/* Auto-opens once for new users with 0 entities; persisted via localStorage per-user */}
      {entities.length === 0 && user?.id && (
        <GettingStartedTour userId={user.id} />
      )}
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit']">
              Welcome back, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-slate-400 mt-1">Here's your corporate structure overview</p>
          </div>
          <Button
            onClick={handleExportAuditReport}
            disabled={exporting}
            variant="outline"
            className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Generating...' : 'Export Audit Report'}
          </Button>

          <Button
            onClick={() => navigate('/formation')}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="build-structure-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Build Structure
          </Button>
          <Button 
            onClick={() => navigate('/entities/new')}
            className="gradient-accent hover:opacity-90"
            data-testid="add-entity-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Entity
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="stat-card" data-testid="stat-total-entities">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-400" />
              </div>
              <span className="text-xs text-slate-500 uppercase tracking-wider">Total</span>
            </div>
            <p className="text-3xl font-bold font-['Outfit']">{stats?.total_entities || 0}</p>
            <p className="text-slate-400 text-sm">Entities</p>
          </div>

          <div className="stat-card" data-testid="stat-ai-recommendations">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-amber-400" />
              </div>
              <span className="text-xs text-slate-500 uppercase tracking-wider">AI</span>
            </div>
            <p className="text-3xl font-bold font-['Outfit']">{stats?.ai_recommendations || 0}</p>
            <p className="text-slate-400 text-sm">Recommendations</p>
          </div>

          <div
            className="stat-card cursor-pointer hover:border-blue-500/50 transition"
            onClick={() => navigate('/formation')}
            data-testid="stat-entity-credits"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Coins className="w-6 h-6 text-emerald-400" />
              </div>
              <span className="text-xs text-emerald-400 uppercase tracking-wider font-medium">Prepaid</span>
            </div>
            <p className="text-3xl font-bold font-['Outfit']" data-testid="credits-remaining">
              {credits?.remaining ?? 0}
            </p>
            <p className="text-slate-400 text-sm">
              Entity credits
              {credits?.total_awarded > 0 && (
                <span className="text-slate-500"> · {credits.used}/{credits.total_awarded} used</span>
              )}
            </p>
            {(credits?.remaining ?? 0) === 0 && (
              <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Buy a package →
              </p>
            )}
          </div>

          <div className="stat-card" data-testid="stat-jurisdictions">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Globe className="w-6 h-6 text-purple-400" />
              </div>
              <span className="text-xs text-slate-500 uppercase tracking-wider">Locations</span>
            </div>
            <p className="text-3xl font-bold font-['Outfit']">
              {Object.keys(stats?.entities_by_jurisdiction || {}).length}
            </p>
            <p className="text-slate-400 text-sm">Jurisdictions</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Entities List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold font-['Outfit']">Your Entities</h2>
              <Button 
                variant="ghost" 
                className="text-blue-400 hover:text-blue-300"
                onClick={() => navigate('/entities')}
                data-testid="view-all-entities-btn"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {entities.length === 0 ? (
              <div className="stat-card text-center py-12" data-testid="no-entities">
                <Network className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No entities yet</h3>
                <p className="text-slate-400 mb-6">Start building your corporate structure</p>
                <Button
                  onClick={() => navigate('/ai-analysis')}
                  className="gradient-accent hover:opacity-90"
                  data-testid="get-ai-recommendation-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Get AI Recommendation
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {entities.slice(0, 5).map((entity) => {
                  const Icon = entityIcons[entity.type] || Building2;
                  const colors = getEntityTypeColor(entity.type);
                  return (
                    <div
                      key={entity.id}
                      className="stat-card flex items-center gap-4 cursor-pointer hover:border-blue-500/50"
                      onClick={() => navigate(`/entities/${entity.id}`)}
                      data-testid={`entity-${entity.id}`}
                    >
                      <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{entity.name}</p>
                        <p className="text-sm text-slate-400">{entity.jurisdiction}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                        {entity.type}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Latest Recommendation */}
            {latestRec && latestRec.recommendations?.[0] && (
              <div className="recommendation-card" data-testid="latest-recommendation">
                <div className="recommendation-header">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    <span className="font-medium">Latest AI Recommendation</span>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Top Jurisdiction</p>
                    <p className="text-lg font-semibold">{latestRec.recommendations[0].jurisdiction}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Key Benefits</p>
                    <div className="space-y-2">
                      {latestRec.recommendations[0].jurisdiction_benefits?.slice(0, 3).map((benefit, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2" />
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button
                    className="w-full bg-slate-800 hover:bg-slate-700"
                    onClick={() => navigate('/ai-analysis')}
                    data-testid="view-recommendation-btn"
                  >
                    View Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Top Jurisdictions */}
            {topJurisdictions.length > 0 && (
              <div className="stat-card" data-testid="top-jurisdictions">
                <h3 className="font-medium mb-4">Top Jurisdictions</h3>
                <div className="space-y-3">
                  {topJurisdictions.map(([jurisdiction, count], i) => (
                    <div key={jurisdiction} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-sm font-medium text-slate-400">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{jurisdiction}</p>
                      </div>
                      <span className="text-sm text-slate-400">{count} entities</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
